import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Card,
  Alert,
  Tooltip,
} from "@mui/material";

import { DoubleArrow, Close } from "@mui/icons-material";
import { themes } from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { CustomButton } from "components/CustomComponents";
import {
  CustomButtonWithBorder,
  CustomButtonWithOutLoader,
} from "components/CustomComponents/CustomButton";
import { CustomAutoComplete } from "components/CustomComponents";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Controller } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { customAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import {
  AssignmentName,
  AssignmentConfigMap,
} from "pages/Administration/AdministrationHeaders";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";

export const BulkAssignmentPanel = ({
  onClose,
  reCallApi,
  isShiftGradePanel,
  isShiftGroupPanel,
}) => {
  const {
    setValue,
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      get_user_data: false,
      current_id: null,
      current_value: "",
      new_id: null,
      new_value: "",
      assignment_name: isShiftGradePanel
        ? "Shift Grade"
        : isShiftGroupPanel
        ? "Shift Group"
        : "Shift Category",
    },
  });
  const { domain } = useSelector((state) => state.tokenReducer);
  const { result: shiftGradeData } = useFetch(
    `${domain}administration/shiftroster/shiftgrade/shift-grades/`
  );
  const { result: shiftGrpData } = useFetch(
    `${domain}administration/shiftroster/shiftgroups/shift-groups/`
  );

  const { result: shiftCategoryData } = useFetch(
    `${domain}administration/shiftroster/shiftrota/shift-categories?status=Active`
  );
  const [currentUsers, setCurrentUsers] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [triggerGradeApi, setTriggerGradeApi] = useState(false);

  const [config, setConfig] = useState("");
  const [openDialogConfimation, setOpenDialogConfimation] = useState(false);
  const [taskProgress, setTaskProgress] = useState({
    active: false,
    progress: 0,
    message: "",
    status: null,
    taskId: null,
    websocketUrl: null,
  });
  const [socket, setSocket] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [formValues, setFormValues] = useState(null);

  useEffect(() => {
    if (!taskProgress.active) return;

    if (
      taskProgress.status === "SUCCESS" ||
      taskProgress.status === "FAILURE"
    ) {
      const timer = setTimeout(() => {
        setTaskProgress((prev) => ({ ...prev, active: false }));
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [taskProgress.status, taskProgress.active]);

  const handleCurrentUserToggle = (id) => {
    setCurrentUsers((prev) =>
      prev.map((u) => (u.user_id === id ? { ...u, checked: !u.checked } : u))
    );
  };

  const handleNewUserToggle = (id) => {
    setNewUsers((prev) =>
      prev.map((u) => (u.user_id === id ? { ...u, checked: !u.checked } : u))
    );
  };

  const handleCurrentSelectAll = (e) => {
    const checked = e.target.checked;
    setCurrentUsers((prev) => prev.map((u) => ({ ...u, checked })));
  };

  const handleNewSelectAll = (e) => {
    const checked = e.target.checked;
    setNewUsers((prev) => prev.map((u) => ({ ...u, checked })));
  };

  const handleMoveToNew = () => {
    const selected = currentUsers.filter((u) => u.checked);
    setNewUsers((prev) => [
      ...prev,
      ...selected.map((u) => ({ ...u, checked: false })),
    ]);
    setCurrentUsers((prev) => prev.filter((u) => !u.checked));
  };

  const handleMoveToCurrent = () => {
    const selected = newUsers.filter((u) => u.checked);
    setCurrentUsers((prev) => [
      ...prev,
      ...selected.map((u) => ({ ...u, checked: false })),
    ]);
    setNewUsers((prev) => prev.filter((u) => !u.checked));
  };

  const updateAssignmentValue = (event, data) => {
    if (data) {
      setValue("assignment_name", data?.label || "");
      setValue("current_value", "");
      setValue("current_id", null);
      setValue("new_value", "");
      setValue("new_id", null);
    }
  };

  const updateCurrentDataValue = (event, data) => {
    if (data) {
      setValue("current_value", data?.label || "");
      setValue("current_id", data?.id || "");
      setValue("new_value", "");
      setValue("new_id", null);
      setNewUsers([]);
      setTriggerGradeApi(true);
    }
  };

  const updateNewDataValue = (event, data) => {
    if (data) {
      setValue("new_value", data?.label || "");
      setValue("new_id", data?.id || null);
    }
  };

  const onSubmit = (data) => {
    setFormValues(data);
    const assignmentName = watch("assignment_name");
    const asgnConfig = AssignmentConfigMap[assignmentName];
    setConfig(asgnConfig);
    setOpenDialogConfimation(true);
  };

  const moveShiftBasedUsers = async () => {
    if (!newUsers || newUsers.length === 0) {
      showToast("error", "No employee selected for this assignment");
      return;
    }

    const userIds = newUsers.map((user) => user.user_id);
    const assignment = watch("assignment_name");

    setValue("button_loader", true);

    const commonDate = watch("effective_from_grade")?.format("YYYY-MM-DD");

    switch (assignment) {
      case "Shift Grade":
        await handleBulkMove(
          `${domain}administration/shiftroster/userassignment/shiftgrade/bulk/move/shiftgrade/`,
          {
            user_ids: userIds,
            old_shift_grade_id: watch("current_id"),
            new_shift_grade_id: watch("new_id"),
            effective_from: commonDate,
          }
        );
        break;

      case "Shift Group":
        await handleBulkMove(
          `${domain}administration/shiftroster/userassignment/shiftgroup/move/shift-groups/`,
          {
            user_ids: userIds,
            shift_group_id: watch("new_id"),
            effective_from: commonDate,
          }
        );
        break;

      case "Shift Category":
        await handleBulkMove(
          `${domain}administration/shiftroster/userassignment/shiftcategory/bulk/move/shift-category/`,
          {
            user_ids: userIds,
            old_shift_category_id: watch("current_id"),
            new_shift_category_id: watch("new_id"),
            effective_from: commonDate,
          }
        );
        break;

      default:
        setValue("button_loader", false);
        return;
    }
  };

  const handleBulkMove = async (url, payload) => {
    try {
      const response = await customAPI(url, "PUT", payload);

      if (!response.ok) {
        throw new Error("Request failed");
      }

      const result = await response.responseDetails;

      if (result?.task_id && result?.websocket_url) {
        setTaskProgress({
          active: true,
          progress: 0,
          message: "Starting background processing...",
          status: "PENDING",
          taskId: result.task_id,
          websocketUrl: result.websocket_url,
        });

        setTimeout(() => {
          connectTaskWebSocket(result.task_id, result.websocket_url);
        }, 5000);
      } else {
        completeUpdateProcess();
      }

      setOpenDialogConfimation(false);
      showToast("success", result?.message);
    } catch (error) {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setValue("button_loader", false);
    }
  };

  useEffect(() => {
    const getUserDetails = async () => {
      if (watch("assignment_name") === "Shift Grade") {
        const res = await customGetAPI(
          `${domain}administration/shiftroster/userassignment/shiftgrade/active-future-users/?shift_grade_id=${watch(
            "current_id"
          )}`
        );
        if (res.ok) {
          setCurrentUsers(
            res?.responseDetails?.data?.map((user) => ({
              ...user,
              checked: false,
            }))
          );
          setTriggerGradeApi(false);
        } else {
          showToast("error", res.error);
        }
      } else if (watch("assignment_name") === "Shift Group") {
        const res = await customGetAPI(
          `${domain}administration/shiftroster/userassignment/shiftgroup/active-future-users/?shift_group_id=${watch(
            "current_id"
          )}`
        );
        if (res.ok) {
          setCurrentUsers(
            res?.responseDetails?.data?.map((user) => ({
              ...user,
              checked: false,
            }))
          );
          setTriggerGradeApi(false);
        } else {
          showToast("error", res.error);
        }
      } else {
        const res = await customGetAPI(
          `${domain}administration/shiftroster/userassignment/shiftcategory/users-based-shift-category/?shift_category_id=${watch(
            "current_id"
          )}`
        );
        if (res.ok) {
          setCurrentUsers(
            res?.responseDetails?.map((user) => ({
              ...user,
              checked: false,
            }))
          );
          setTriggerGradeApi(false);
        } else {
          showToast("error", res.error);
        }
      }
    };
    if (watch("current_id") && triggerGradeApi) {
      getUserDetails();
      setTriggerGradeApi(false);
    }
  }, [domain, setValue, triggerGradeApi, watch]);

  const handleDeactivateDialogBox = () => {
    setOpenDialogConfimation(false);
    setButtonLoader(false);
  };

  const connectTaskWebSocket = (taskId, websocketUrl) => {
    if (socket) {
      socket.close();
    }

    const newSocket = new WebSocket(websocketUrl);
    setSocket(newSocket);

    newSocket.onopen = () => {
      console.log("WebSocket connected for task:", taskId);
    };

    newSocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket progress update:", data);

        setTaskProgress((prev) => ({
          ...prev,
          progress: data.progress || prev.progress,
          message: data.message || prev.message,
          status: data.status || prev.status,
        }));

        if (data.status === "SUCCESS") {
          showToast("success", "Background processing completed successfully");
          setTimeout(() => {
            completeUpdateProcess();
          }, 2000);
        } else if (data.status === "FAILURE") {
          showToast(
            "error",
            `Background processing failed: ${data.error || "Unknown error"}`
          );
          setTimeout(() => {
            setTaskProgress((prev) => ({ ...prev, active: false }));
          }, 5000);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    newSocket.onerror = (error) => {
      console.error("WebSocket error:", error);
      showToast("warning", "Connection to progress updates interrupted");
      setTaskProgress((prev) => ({
        ...prev,
        message: "Connection issues - processing continues in background",
        status: "PROGRESS",
      }));
    };

    newSocket.onclose = () => {
      console.log("WebSocket closed for task:", taskId);
      if (taskProgress.status !== "SUCCESS") {
        setTimeout(() => {
          setTaskProgress((prev) => ({ ...prev, active: false }));
        }, 3000);
      }
    };
  };

  const completeUpdateProcess = () => {
    reCallApi(true);
    onClose();
    setValue("button_loader", false);
  };

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Paper
            elevation={0}
            sx={{
              borderBottom: `1px solid ${themes?.miliedLightGray}`,
              borderRadius: 0,
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              sx={{ padding: 1, backgroundColor: themes?.primary }}
            >
              <Box sx={{ ml: 1 }}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    color: themes?.grayColor,
                    "&:hover": {
                      backgroundColor: themes?.whiteColor,
                    },
                  }}
                >
                  <DoubleArrow />
                </IconButton>
              </Box>

              <Box sx={{ ml: 2.2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Change User Assignment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and edit user shift assignment
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    ml: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Select Assignment{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <CustomAutoComplete
                      dataList={AssignmentName?.map((obj) => ({
                        ...obj,
                        label: obj?.label,
                      }))}
                      rules={{
                        ...register("assignment_name", {
                          required: "Assignment type is required",
                        }),
                      }}
                      selectedValue={watch("assignment_name")}
                      updateDataFunction={updateAssignmentValue}
                      placeHolder="Select the assignment name"
                      errorMessage={
                        errors?.assignment_name?.message
                          ? errors?.assignment_name?.message
                          : ""
                      }
                      textBoxError={errors?.assignment_name ? true : false}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Effective From{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer
                        components={["DatePicker"]}
                        style={{ width: "100%" }}
                      >
                        <Controller
                          name="effective_from_grade"
                          control={control}
                          rules={{
                            validate: (val) => {
                              if (!val)
                                return "Effective From date is required";
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              value={field.value || null}
                              onChange={(date) => field.onChange(date)}
                              format="DD/MM/YYYY"
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                  error: !!errors?.effective_from_grade,
                                  helperText:
                                    errors?.effective_from_grade?.message,
                                },
                              }}
                            />
                          )}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    ml: 1.5,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Current Assignment Details
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      New Assignment Details
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    ml: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {watch("assignment_name") === "Shift Grade"
                        ? "Current Shift Grade"
                        : watch("assignment_name") === "Shift Group"
                        ? "Current Shift Group"
                        : "Current Shift Category"}{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <CustomAutoComplete
                      dataList={
                        watch("assignment_name") === "Shift Grade"
                          ? shiftGradeData?.map((obj) => ({
                              ...obj,
                              label: obj?.name,
                            }))
                          : watch("assignment_name") === "Shift Group"
                          ? shiftGrpData?.map((obj) => ({
                              ...obj,
                              label: obj?.name,
                            }))
                          : watch("assignment_name") === "Shift Category"
                          ? shiftCategoryData?.map((obj) => ({
                              ...obj,
                              label: obj?.name,
                            }))
                          : []
                      }
                      rules={{
                        ...register("current_value", {
                          required:
                            watch("assignment_name") === "Shift Grade"
                              ? "Shift grade is required"
                              : watch("assignment_name") === "Shift Group"
                              ? "Shift group is required"
                              : "Shift category is required",
                        }),
                      }}
                      selectedValue={watch("current_value")}
                      updateDataFunction={updateCurrentDataValue}
                      placeHolder={
                        watch("assignment_name") === "Shift Grade"
                          ? "Select the shift grade"
                          : watch("assignment_name") === "Shift Group"
                          ? "Select the shift group"
                          : "Select the shift category"
                      }
                      errorMessage={
                        errors?.current_value?.message
                          ? errors?.current_value?.message
                          : ""
                      }
                      textBoxError={errors?.current_value ? true : false}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {watch("assignment_name") === "Shift Grade"
                        ? "New Shift Grade To Be Assigned"
                        : watch("assignment_name") === "Shift Group"
                        ? "New Shift Group To Be Assigned"
                        : "New Shift Category To Be Assigned"}{" "}
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <CustomAutoComplete
                      dataList={
                        watch("assignment_name") === "Shift Grade"
                          ? shiftGradeData
                              ?.filter((obj) => obj?.id !== watch("current_id"))
                              ?.map((obj) => ({
                                ...obj,
                                label: obj?.name,
                              }))
                          : watch("assignment_name") === "Shift Group"
                          ? shiftGrpData
                              ?.filter((obj) => obj?.id !== watch("current_id"))
                              ?.map((obj) => ({
                                ...obj,
                                label: obj?.name,
                              }))
                          : watch("assignment_name") === "Shift Category"
                          ? shiftCategoryData
                              ?.filter((obj) => obj?.id !== watch("current_id"))
                              ?.map((obj) => ({
                                ...obj,
                                label: obj?.name,
                              }))
                          : []
                      }
                      rules={{
                        ...register("new_value", {
                          required:
                            watch("assignment_name") === "Shift Grade"
                              ? "New shift grade is required"
                              : watch("assignment_name") === "Shift Group"
                              ? "New shift group is required"
                              : "New shift category is required",
                        }),
                      }}
                      selectedValue={watch("new_value")}
                      updateDataFunction={updateNewDataValue}
                      placeHolder={
                        watch("assignment_name") === "Shift Grade"
                          ? "Select the new shift grade"
                          : watch("assignment_name") === "Shift Group"
                          ? "Select the new shift group"
                          : "Select the new shift category"
                      }
                      errorMessage={
                        errors?.new_value?.message
                          ? errors?.new_value?.message
                          : ""
                      }
                      textBoxError={errors?.new_value ? true : false}
                    />
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ borderColor: themes?.miliedLightGray }} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1.5,
                  mb: 2,
                  alignItems: "center",
                }}
              >
                <Card
                  sx={{
                    width: 300,
                    height: 480,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          sx={{ p: 0, ml: 3 }}
                          checked={
                            currentUsers.length > 0 &&
                            currentUsers.every((u) => u.checked)
                          }
                          indeterminate={
                            currentUsers.some((u) => u.checked) &&
                            !currentUsers.every((u) => u.checked)
                          }
                          onChange={handleCurrentSelectAll}
                        />
                      }
                      label="Select all"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          textTransform: "uppercase",
                          color: "success.main",
                          marginLeft: "8px",
                        },
                      }}
                    />
                  </Box>
                  <Divider />
                  <Box sx={{ flex: 1, overflowY: "auto" }}>
                    {currentUsers?.length === 0 ? (
                      <Box sx={{ mt: 5 }}>
                        <Alert sx={{ width: "100%" }} severity="info">
                          No employees available
                        </Alert>
                      </Box>
                    ) : (
                      currentUsers.map((user, index) => (
                        <Box key={user.user_id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={user.checked}
                                onChange={() =>
                                  handleCurrentUserToggle(user.user_id)
                                }
                              />
                            }
                            label={user.employee_name}
                            sx={{ pl: 2 }}
                          />
                          {index < currentUsers?.length - 1 && <Divider />}
                        </Box>
                      ))
                    )}
                  </Box>
                </Card>
                <Card
                  sx={{
                    width: 300,
                    height: 480,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          sx={{ p: 0, ml: 3 }}
                          checked={
                            newUsers?.length > 0 &&
                            newUsers.every((u) => u.checked)
                          }
                          indeterminate={
                            newUsers.some((u) => u.checked) &&
                            !newUsers.every((u) => u.checked)
                          }
                          onChange={handleNewSelectAll}
                        />
                      }
                      label="Select all"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          textTransform: "uppercase",
                          color: "success.main",
                          marginLeft: "8px",
                        },
                      }}
                    />
                  </Box>
                  <Divider />
                  <Box sx={{ flex: 1, overflowY: "auto" }}>
                    {newUsers?.length === 0 ? (
                      <Box sx={{ mt: 5 }}>
                        <Alert sx={{ width: "100%" }} severity="info">
                          No employees available
                        </Alert>
                      </Box>
                    ) : (
                      newUsers.map((user, index) => (
                        <Box key={user.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={user.checked}
                                onChange={() =>
                                  handleNewUserToggle(user.user_id)
                                }
                              />
                            }
                            label={user.employee_name}
                            sx={{ pl: 2 }}
                          />
                          {index < newUsers?.length - 1 && <Divider />}
                        </Box>
                      ))
                    )}
                  </Box>
                </Card>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 4,
                ml: 1.5,
              }}
            >
              <Box display="flex" justifyContent="flex-end">
                <Tooltip title="Move" arrow slotProps={tooltipStyleProps}>
                  <ArrowCircleRightIcon
                    sx={{
                      cursor: currentUsers.some((u) => u.checked)
                        ? "pointer"
                        : "not-allowed",
                      color: currentUsers.some((u) => u.checked)
                        ? themes?.mutedgreen
                        : themes?.redColor,
                      fontSize: "30px",
                    }}
                    onClick={
                      currentUsers.some((u) => u.checked)
                        ? handleMoveToNew
                        : undefined
                    }
                  />
                </Tooltip>
              </Box>
              <Box>
                <Tooltip title="Move" arrow slotProps={tooltipStyleProps}>
                  <ArrowCircleLeftIcon
                    sx={{
                      cursor: newUsers.some((u) => u.checked)
                        ? "pointer"
                        : "not-allowed",
                      color: newUsers.some((u) => u.checked)
                        ? themes?.mutedgreen
                        : themes?.redColor,
                      fontSize: "30px",
                    }}
                    onClick={
                      newUsers.some((u) => u.checked)
                        ? handleMoveToCurrent
                        : undefined
                    }
                  />
                </Tooltip>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              {newUsers?.length === 0 ? (
                <CustomButtonWithOutLoader
                  actionName="Move"
                  typeName="button"
                  disableAction={true}
                />
              ) : (
                <CustomButton
                  actionName="Move"
                  typeName="submit"
                  disableAction={watch("button_loader")}
                />
              )}
            </Box>
          </Box>
        </form>
      </Box>

      <Mui.Dialog
        open={openDialogConfimation}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleDeactivateDialogBox();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            width: 650,
            maxWidth: "100%",
            borderRadius: 2,
          },
        }}
      >
        <Mui.DialogTitle id="alert-dialog-title">
          {config?.title}
          <IconButton
            aria-label="close"
            onClick={handleDeactivateDialogBox}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Mui.DialogTitle>
        <Mui.Divider />

        <Mui.DialogContent>
          <Mui.DialogContentText id="alert-dialog-description">
            {config?.content}
          </Mui.DialogContentText>
        </Mui.DialogContent>

        <Mui.DialogActions sx={{ mr: 2, mb: 1 }}>
          <CustomButtonWithBorder
            actionName={"Cancel"}
            actionFuntion={handleDeactivateDialogBox}
          />

          <CustomButtonWithBorder
            actionName={"Confirm"}
            actionFuntion={() => {
              moveShiftBasedUsers(formValues);
            }}
            disableAction={
              openDialogConfimation && buttonLoader ? buttonLoader : false
            }
            handleColor={true}
          />
        </Mui.DialogActions>
      </Mui.Dialog>

      <Mui.Dialog
        open={taskProgress.active}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        aria-labelledby="progress-dialog-title"
        PaperProps={{
          sx: {
            width: 650,
            maxWidth: "100%",
            borderRadius: 2,
          },
        }}
      >
        <Mui.DialogTitle id="progress-dialog-title" sx={{ pb: 1 }}>
          <Mui.Grid
            container
            justifyContent="space-between"
            alignItems="center"
          >
            <Mui.Grid item display="flex" alignItems="center" gap={1}>
              <Mui.Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor:
                    taskProgress.status === "SUCCESS"
                      ? "#10B981"
                      : taskProgress.status === "FAILURE"
                      ? "#EF4444"
                      : socket?.readyState === WebSocket.OPEN
                      ? "#10B981"
                      : "#F59E0B",
                }}
              />
              <Mui.Typography variant="subtitle1" fontWeight={700}>
                Processing Changes
              </Mui.Typography>
            </Mui.Grid>

            <Mui.Grid item>
              <Mui.Typography variant="body2" color="text.secondary">
                {taskProgress.status === "PROGRESS"
                  ? "In Progress"
                  : taskProgress.status === "SUCCESS"
                  ? "Completed"
                  : taskProgress.status === "FAILURE"
                  ? "Failed"
                  : "Pending"}
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogTitle>

        <Mui.Divider />

        <Mui.DialogContent sx={{ mt: 1 }}>
          <Mui.Box sx={{ width: "100%" }}>
            <Mui.LinearProgress
              variant="determinate"
              value={taskProgress.progress}
              sx={{
                height: 12,
                borderRadius: 1,
                backgroundColor: "#E5E7EB",
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    taskProgress.status === "FAILURE"
                      ? "#EF4444"
                      : taskProgress.status === "SUCCESS"
                      ? "#10B981"
                      : "#3B82F6",
                },
              }}
            />
          </Mui.Box>

          <Mui.Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 1 }}
          >
            <Mui.Typography variant="body2" color="text.secondary">
              {taskProgress.message}
            </Mui.Typography>
            <Mui.Typography variant="body2" fontWeight={500}>
              {taskProgress.progress}%
            </Mui.Typography>
          </Mui.Grid>
        </Mui.DialogContent>
        <Mui.DialogActions sx={{ mr: 2, mb: 1 }}></Mui.DialogActions>
      </Mui.Dialog>
    </>
  );
};
